import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, List, Input, message, Image, Pagination, Button } from 'antd';
import { useParams } from 'react-router-dom';
import { useFlowStore } from '../store';
import {
  FlagOutlined,
  FlagFilled,
  YoutubeOutlined,
  LikeOutlined
} from '@ant-design/icons';
import AuthenticatedLayout from '../layout/AuthenticatedLayout';
import { format, parseISO } from 'date-fns';
import _ from 'lodash';
import { ResponseUserInfoInner } from '../../api/api';
import { KeyAdmin, KeyReputationEditor } from '../constants';
import UserVideo from './UserVideo';

const { Text, Title } = Typography;

const UserPage = () => {
  const { viewedUserDetails, getViewedUser, getUser, putUser, clearError, error, userDetails, role } = useFlowStore();
  let reputation: number | undefined;
  let description: string | undefined;
  let location: string | undefined;
  let isFlagged: boolean | undefined;

  const { id } = useParams<{ id: string }>();

  const [isEditing, setIsEditing] = useState(false);
  const [localIsFlagged, setIsFlagged] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [localReputation, setReputation] = useState(0);
  const [localLocation, setLocalLocation] = useState("");
  const [localDescription, setLocalDescription] = useState("");
  const [messageApi, contextHolder] = message.useMessage();

  // Add pagination state for each section
  const [linkedPage, setLinkedPage] = useState(1);
  const [videoUpPage, setVideoUpPage] = useState(1);
  const [videoDownPage, setVideoDownPage] = useState(1);
  const [battleTestedUpPage, setBattleTestedUpPage] = useState(1);
  const [battleTestedDownPage, setBattleTestedDownPage] = useState(1);
  const [freshUpPage, setFreshUpPage] = useState(1);
  const [freshDownPage, setFreshDownPage] = useState(1);
  const [createdPage, setCreatedPage] = useState(1);
  const [editedPage, setEditedPage] = useState(1);
  
  // Items per page - 2 rows with approximately 6 items per row for videos
  const videoPageSize = 12;
  // Items per page for activity sections - 2 rows with 4 items per row
  const activityPageSize = 8;

  const errorDisplayer = (content: string | React.ReactNode) => {
    messageApi.open({
      type: 'error',
      content: content,
      duration: 2.5,
    })
    .then(() => clearError());
  };

  useEffect(() => {
    if (error != null) errorDisplayer(error);
  }, [error]);

  const onClickFlag = async () => {
    const data = viewedUserDetails!;
    data.isFlagged = !localIsFlagged;
    data.location = localLocation;
    data.description = localDescription;
    const success = await putUser(data);
    if (success) setIsFlagged(!localIsFlagged);
  };

  const onClickEdit = async () => {
    if (isEditing) {
      const data = viewedUserDetails!;
      data.isFlagged = localIsFlagged;
      data.location = localLocation;
      data.description = localDescription;
      const success = await putUser(data);
      if (success) setIsEditing(!isEditing);
    }
    else {
      setIsEditing(!isEditing);
    }
  };

  useEffect(() => {
    if(id) getViewedUser(id);
    if (userDetails?.id) getUser(userDetails?.id);
    
    // Check if current user is admin or has editor reputation
    const isAdmin = role === KeyAdmin;
    const hasEditorReputation = (userDetails?.reputation ?? 0) >= KeyReputationEditor;
    
    // Check if the user is viewing their own profile
    const isOwnProfile = userDetails?.id === id;
    
    // Allow editing if:
    // 1. User is admin, OR
    // 2. User has sufficient reputation, OR
    // 3. User is viewing their own profile
    setIsEditable(isAdmin || hasEditorReputation || isOwnProfile);
  }, [id, getViewedUser, userDetails?.id, userDetails?.role, userDetails?.reputation]);

  useEffect(() => {
    // Make sure we have arrays for all the list data, even if they're empty
    reputation = viewedUserDetails?.reputation || 0;
    description = viewedUserDetails?.description || '';
    location = viewedUserDetails?.location || '';
    isFlagged = viewedUserDetails?.isFlagged || false;

    setIsFlagged(isFlagged);
    setLocalLocation(location);
    setLocalDescription(description);
    setReputation(reputation);
  }, [viewedUserDetails]);

  if (viewedUserDetails === null) {
    return <h1>User details not found</h1>;
  }

  const renderList = (isVideo: boolean, items?: ResponseUserInfoInner[] | string[]) => {

    if (!items || items.length === 0) {
      return <Text>No items available</Text>;
    }

    return (
      <List
        size="small"
        bordered
        dataSource={items as any[]}
        renderItem={(item) => (
          <List.Item>
            {isVideo ? displayVideoInfo(item) : displayNodeInfo(item as ResponseUserInfoInner, false)}
          </List.Item>
        )}
      />
    );
  };

  const displayVideoInfo = (item: any): React.JSX.Element => {
    // Check if item is valid
    if (!item) {
      return <Text>Invalid video data</Text>;
    }

    // Handle both string URLs and objects with link property
    const videoUrl = typeof item === 'string' ? item : item.id || item.link;

    if (!videoUrl) {
      return <Text>Invalid video URL</Text>;
    }

    // Extract video ID for thumbnail
    const extractVideoId = (url: string) => {
      try {
        const urlObj = new URL(url);
        if (urlObj.hostname === 'www.youtube.com' && urlObj.pathname === '/watch') {
          // Extract video ID from the 'v' parameter for full URLs
          return urlObj.searchParams.get('v');
        } else if (urlObj.hostname === 'youtu.be') {
          // Extract video ID from the path for shortened URLs
          return urlObj.pathname.slice(1);
        }
        return null; // Not a valid YouTube URL
      } catch (error) {
        console.error("Invalid URL:", error);
        return null;
      }
    };

    const videoId = extractVideoId(videoUrl);
    const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : undefined;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <a
          href={videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'block', width: '100%', textAlign: 'center' }}
        >
          {thumbnailUrl ? (
            <Image
              src={thumbnailUrl}
              alt="YouTube video"
              style={{ width: '100%', maxWidth: '200px', height: 'auto', objectFit: 'cover' }}
              preview={false}
            />
          ) : (
            <div style={{ 
              width: '100%', 
              maxWidth: '200px', 
              height: '120px', 
              background: '#f0f0f0', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <YoutubeOutlined style={{ fontSize: '32px', color: '#ff0000' }} />
            </div>
          )}
        </a>
      </div>
    );
  };

  const displayNodeInfo = (nodeInfo: ResponseUserInfoInner, isVideo: boolean): React.JSX.Element => {
    if (isVideo) {
      return displayVideoInfo(nodeInfo);
    }

    // Check if nodeInfo has the required properties
    if (!nodeInfo) {
      return <Text>Invalid node data</Text>;
    }

    if (!nodeInfo.topic || !nodeInfo.nodeId) {
      return <Text>{JSON.stringify(nodeInfo)}</Text>;
    }

    return (
      <a href={`/${nodeInfo.topic}/wiki-page/${encodeURIComponent(String(nodeInfo.nodeId))}`}>
        {nodeInfo.title || `${nodeInfo.topic} - ${nodeInfo.nodeId}`}
      </a>
    );
  };



  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch (e) {
      return dateString;
    }
  };

  const getYouTubeThumbnail = (url: string | undefined): string => {
  if (!url) return 'https://via.placeholder.com/120x90?text=No+Video';

    const extractVideoId = (url: string) => {
      try {
        const urlObj = new URL(url);

        // Standard YouTube watch URL
        if (urlObj.hostname.includes('youtube.com') && urlObj.pathname === '/watch') {
          return urlObj.searchParams.get('v');
        }

        // Shortened youtu.be URL
        if (urlObj.hostname === 'youtu.be') {
          return urlObj.pathname.slice(1);
        }

        // Shorts URL
        if (urlObj.hostname.includes('youtube.com') && urlObj.pathname.startsWith('/shorts/')) {
          const pathSegments = urlObj.pathname.split('/').filter(Boolean);
          return pathSegments.length > 1 ? pathSegments[1] : null;
        }

        return null;
      } catch (error) {
        console.error("Invalid URL:", error);
        return null;
      }
    };

    const videoId = extractVideoId(url);
    if (!videoId) return 'https://via.placeholder.com/120x90?text=Invalid+URL';

    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  };

  return (
    <AuthenticatedLayout>
      <div style={{ padding: '24px' }}>
        {contextHolder}
        <Row gutter={[16, 16]} align="middle">
          
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              {viewedUserDetails?.username}
              {!isEditing && (
                localIsFlagged ?
                  <FlagFilled className='pop-button' onClick={onClickFlag} style={{ marginLeft: 8, color: '#ff4d4f' }} /> :
                  <FlagOutlined className='pop-button' onClick={onClickFlag} style={{ marginLeft: 8 }} />
              )}
            </Title>
            <Text type="secondary">
              Joined {formatDate(viewedUserDetails?.createdAt || '')} •
              Last login {formatDate(viewedUserDetails?.lastLogin || '')}
            </Text>
          </Col>
          <Col flex="auto"></Col>
          <Col>
            {isEditable && (
              <button
                onClick={onClickEdit}
                style={{
                  padding: '4px 15px',
                  borderRadius: '2px',
                  border: '1px solid #d9d9d9',
                  backgroundColor: isEditing ? '#1890ff' : '#fff',
                  color: isEditing ? '#fff' : 'inherit',
                  cursor: 'pointer'
                }}
              >
                {isEditing ? 'Save' : 'Edit'}
              </button>
            )}
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          {/* Basic User Info */}
          <Col xs={24} sm={12} md={8} lg={8}>
            <Card title="Reputation" bordered>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <LikeOutlined style={{ fontSize: 20, marginRight: 8, color: '#52c41a' }} />
                <Text style={{ fontSize: 18 }}>{localReputation || 0}</Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Card title="Location" bordered>
              {isEditing ?
                <Input
                  defaultValue={localLocation}
                  onChange={(e) => {
                    setLocalLocation(e.target.value);
                  }}
                /> :
                <Text>{localLocation || 'Unknown'}</Text>
              }
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Card title="Description" bordered>
              {isEditing ?
                <Input
                  defaultValue={localDescription}
                  onChange={(e) => {
                    setLocalDescription(e.target.value);
                  }}
                /> :
                <Text>{localDescription || 'No description provided'}</Text>
              }
            </Card>
          </Col>

          {/* Activity Sections */}
          <Col xs={24}>
            <Title level={3} style={{ margin: '24px 0 16px 0' }}>User Activity</Title>
          </Col>

          {/* Battle Tested Up */}
          <Col xs={24} sm={12} md={12} lg={12}>
            <Card 
              title="Battle Tested Up" 
              bordered
            >
              {viewedUserDetails?.battleTestedUp && viewedUserDetails.battleTestedUp.length > 0 ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                    {viewedUserDetails.battleTestedUp
                      .slice((battleTestedUpPage - 1) * activityPageSize, battleTestedUpPage * activityPageSize)
                      .map((item, index) => (
                        <div key={index} style={{ border: '1px solid #f0f0f0', padding: '8px', borderRadius: '4px' }}>
                          {item.nodeId && item.nodeId !== "0001-01-01T00:00:00Z" ? (
                            <a href={`/${item.topic}/wiki-page/${encodeURIComponent(String(item.nodeId))}`}>
                              {item.title || `${item.topic} - ${item.nodeId}`}
                            </a>
                          ) : (
                            <span>{item.title || `${item.topic} (Link unavailable)`}</span>
                          )}
                        </div>
                      ))}
                  </div>
                  {viewedUserDetails.battleTestedUp.length > activityPageSize && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <Pagination 
                        simple
                        current={battleTestedUpPage}
                        onChange={setBattleTestedUpPage}
                        total={viewedUserDetails.battleTestedUp.length}
                        showQuickJumper
                        showSizeChanger={false}
                        pageSize={activityPageSize}
                      />
                    </div>
                  )}
                </>
              ) : (
                <Text>No items available</Text>
              )}
            </Card>
          </Col>

          {/* Battle Tested Down */}
          <Col xs={24} sm={12} md={12} lg={12}>
            <Card 
              title="Battle Tested Down" 
              bordered
            >
              {viewedUserDetails?.battleTestedDown && viewedUserDetails.battleTestedDown.length > 0 ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                    {viewedUserDetails.battleTestedDown
                      .slice((battleTestedDownPage - 1) * activityPageSize, battleTestedDownPage * activityPageSize)
                      .map((item, index) => (
                        <div key={index} style={{ border: '1px solid #f0f0f0', padding: '8px', borderRadius: '4px' }}>
                          {item.nodeId && item.nodeId !== "0001-01-01T00:00:00Z" ? (
                            <a href={`/${item.topic}/wiki-page/${encodeURIComponent(String(item.nodeId))}`}>
                              {item.title || `${item.topic} - ${item.nodeId}`}
                            </a>
                          ) : (
                            <span>{item.title || `${item.topic} (Link unavailable)`}</span>
                          )}
                        </div>
                      ))}
                  </div>
                  {viewedUserDetails.battleTestedDown.length > activityPageSize && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <Pagination 
                        simple
                        current={battleTestedDownPage}
                        onChange={setBattleTestedDownPage}
                        total={viewedUserDetails.battleTestedDown.length}
                        showQuickJumper
                        showSizeChanger={false}
                        pageSize={activityPageSize}
                      />
                    </div>
                  )}
                </>
              ) : (
                <Text>No items available</Text>
              )}
            </Card>
          </Col>

          {/* Fresh Up */}
          <Col xs={24} sm={12} md={12} lg={12}>
            <Card 
              title="Fresh Up" 
              bordered
            >
              {viewedUserDetails?.freshUp && viewedUserDetails.freshUp.length > 0 ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                    {viewedUserDetails.freshUp
                      .slice((freshUpPage - 1) * activityPageSize, freshUpPage * activityPageSize)
                      .map((item, index) => (
                        <div key={index} style={{ border: '1px solid #f0f0f0', padding: '8px', borderRadius: '4px' }}>
                          {item.nodeId && item.nodeId !== "0001-01-01T00:00:00Z" ? (
                            <a href={`/${item.topic}/wiki-page/${encodeURIComponent(String(item.nodeId))}`}>
                              {item.title || `${item.topic} - ${item.nodeId}`}
                            </a>
                          ) : (
                            <span>{item.title || `${item.topic} (Link unavailable)`}</span>
                          )}
                        </div>
                      ))}
                  </div>
                  {viewedUserDetails.freshUp.length > activityPageSize && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <Pagination 
                        simple
                        current={freshUpPage}
                        onChange={setFreshUpPage}
                        total={viewedUserDetails.freshUp.length}
                        showQuickJumper
                        showSizeChanger={false}
                        pageSize={activityPageSize}
                      />
                    </div>
                  )}
                </>
              ) : (
                <Text>No items available</Text>
              )}
            </Card>
          </Col>

          {/* Fresh Down */}
          <Col xs={24} sm={12} md={12} lg={12}>
            <Card 
              title="Fresh Down" 
              bordered
            >
              {viewedUserDetails?.freshDown && viewedUserDetails.freshDown.length > 0 ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                    {viewedUserDetails.freshDown
                      .slice((freshDownPage - 1) * activityPageSize, freshDownPage * activityPageSize)
                      .map((item, index) => (
                        <div key={index} style={{ border: '1px solid #f0f0f0', padding: '8px', borderRadius: '4px' }}>
                          {item.nodeId && item.nodeId !== "0001-01-01T00:00:00Z" ? (
                            <a href={`/${item.topic}/wiki-page/${encodeURIComponent(String(item.nodeId))}`}>
                              {item.title || `${item.topic} - ${item.nodeId}`}
                            </a>
                          ) : (
                            <span>{item.title || `${item.topic} (Link unavailable)`}</span>
                          )}
                        </div>
                      ))}
                  </div>
                  {viewedUserDetails.freshDown.length > activityPageSize && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <Pagination 
                        simple
                        current={freshDownPage}
                        onChange={setFreshDownPage}
                        total={viewedUserDetails.freshDown.length}
                        showQuickJumper
                        showSizeChanger={false}
                        pageSize={activityPageSize}
                      />
                    </div>
                  )}
                </>
              ) : (
                <Text>No items available</Text>
              )}
            </Card>
          </Col>

          {/* Created Nodes */}
          <Col xs={24} sm={12} md={12} lg={12}>
            <Card 
              title="Created Nodes" 
              bordered
            >
              {viewedUserDetails?.created && viewedUserDetails.created.length > 0 ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                    {viewedUserDetails.created
                      .slice((createdPage - 1) * activityPageSize, createdPage * activityPageSize)
                      .map((item, index) => (
                        <div key={index} style={{ border: '1px solid #f0f0f0', padding: '8px', borderRadius: '4px' }}>
                          {item.nodeId && item.nodeId !== "0001-01-01T00:00:00Z" ? (
                            <a href={`/${item.topic}/wiki-page/${encodeURIComponent(String(item.nodeId))}`}>
                              {item.title || `${item.topic} - ${item.nodeId}`}
                            </a>
                          ) : (
                            <span>{item.title || `${item.topic} (Link unavailable)`}</span>
                          )}
                        </div>
                      ))}
                  </div>
                  {viewedUserDetails.created.length > activityPageSize && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <Pagination 
                        simple
                        current={createdPage}
                        onChange={setCreatedPage}
                        total={viewedUserDetails.created.length}
                        showQuickJumper
                        showSizeChanger={false}
                        pageSize={activityPageSize}
                      />
                    </div>
                  )}
                </>
              ) : (
                <Text>No items available</Text>
              )}
            </Card>
          </Col>

          {/* Edited Nodes */}
          <Col xs={24} sm={12} md={12} lg={12}>
            <Card 
              title="Edited Nodes" 
              bordered
            >
              {viewedUserDetails?.edited && viewedUserDetails.edited.length > 0 ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                    {viewedUserDetails.edited
                      .slice((editedPage - 1) * activityPageSize, editedPage * activityPageSize)
                      .map((item, index) => (
                        <div key={index} style={{ border: '1px solid #f0f0f0', padding: '8px', borderRadius: '4px' }}>
                          {item.nodeId && item.nodeId !== "0001-01-01T00:00:00Z" ? (
                            <a href={`/${item.topic}/wiki-page/${encodeURIComponent(String(item.nodeId))}`}>
                              {item.title || `${item.topic} - ${item.nodeId}`}
                            </a>
                          ) : (
                            <span>{item.title || `${item.topic} (Link unavailable)`}</span>
                          )}
                        </div>
                      ))}
                  </div>
                  {viewedUserDetails.edited.length > activityPageSize && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <Pagination 
                        simple
                        current={editedPage}
                        onChange={setEditedPage}
                        total={viewedUserDetails.edited.length}
                        showQuickJumper
                        showSizeChanger={false}
                        pageSize={activityPageSize}
                      />
                    </div>
                  )}
                </>
              ) : (
                <Text>No items available</Text>
              )}
            </Card>
          </Col>

          {/* Videos Section - Moved to bottom */}
          <Col xs={24}>
            <Title level={3} style={{ margin: '24px 0 16px 0' }}>Videos</Title>
          </Col>

          {/* Videos Added */}
          <Col xs={24}>
            <Card
              title="Videos Added"
              bordered
              extra={<Text type="secondary">{viewedUserDetails?.linked?.length || 0} videos</Text>}
            >
              {viewedUserDetails?.linked && viewedUserDetails?.linked.length > 0 ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px' }}>
                    {viewedUserDetails.linked
                      .slice((linkedPage - 1) * videoPageSize, linkedPage * videoPageSize)
                      .map((video: any, index: number) => (
                        <div
                          key={index}
                          style={{ cursor: 'pointer' }}
                        >
                          <div style={{ overflow: 'hidden' }}>
                            <UserVideo video={video.link || video.id} />
                          </div>
                        </div>
                      ))}
                  </div>
                  {viewedUserDetails.linked.length > videoPageSize && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <Pagination 
                        simple
                        current={linkedPage}
                        onChange={setLinkedPage}
                        total={viewedUserDetails.linked.length}
                        showQuickJumper
                        showSizeChanger={false}
                        pageSize={videoPageSize}
                      />
                    </div>
                  )}
                </>
              ) : (
                <Text>No videos added</Text>
              )}
            </Card>
          </Col>

          {/* Videos Upvoted */}
          <Col xs={24} md={12}>
            <Card 
              title="Videos Upvoted" 
              bordered
              extra={<Text type="secondary">{viewedUserDetails?.videoUp?.length || 0} videos</Text>}
            >
              {viewedUserDetails?.videoUp && viewedUserDetails.videoUp.length > 0 ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px' }}>
                    {viewedUserDetails.videoUp
                      .slice((videoUpPage - 1) * videoPageSize, videoUpPage * videoPageSize)
                      .map((video: any, index: number) => (
                        <div
                          key={index}
                          style={{ cursor: 'pointer' }}
                        >
                          <div style={{ overflow: 'hidden' }}>
                            <UserVideo video={video} />
                          </div>
                        </div>
                      ))}
                  </div>
                  {viewedUserDetails.videoUp.length > videoPageSize && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <Pagination 
                        simple
                        current={videoUpPage}
                        onChange={setVideoUpPage}
                        total={viewedUserDetails.videoUp.length}
                        showQuickJumper
                        showSizeChanger={false}
                        pageSize={videoPageSize}
                      />
                    </div>
                  )}
                </>
              ) : (
                <Text>No videos upvoted</Text>
              )}
            </Card>
          </Col>

          {/* Videos Downvoted */}
          <Col xs={24} md={12}>
            <Card 
              title="Videos Downvoted" 
              bordered
              extra={<Text type="secondary">{viewedUserDetails?.videoDown?.length || 0} videos</Text>}
            >
              {viewedUserDetails?.videoDown && viewedUserDetails.videoDown.length > 0 ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px' }}>
                    {viewedUserDetails.videoDown
                      .slice((videoDownPage - 1) * videoPageSize, videoDownPage * videoPageSize)
                      .map((video: any, index: number) => (
                        <div
                          key={index}
                          style={{ cursor: 'pointer' }}
                        >
                          <div style={{ overflow: 'hidden' }}>
                            <UserVideo video={video} />
                          </div>
                        </div>
                      ))}
                  </div>
                  {viewedUserDetails.videoDown.length > videoPageSize && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <Pagination 
                        simple
                        current={videoDownPage}
                        onChange={setVideoDownPage}
                        total={viewedUserDetails.videoDown.length}
                        showQuickJumper
                        showSizeChanger={false}
                        pageSize={videoPageSize}
                      />
                    </div>
                  )}
                </>
              ) : (
                <Text>No videos downvoted</Text>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </AuthenticatedLayout>
  );
};

export default UserPage;
